import { ElementRef, PLATFORM_ID, inject } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { filter, take } from "rxjs";
import { ChatMessage } from "../../core/models/chat.model";
import {
  ChatPersistenceService,
  PersistedChatState,
} from "../../core/services/chat-persistence.service";
import { BrowserStorageService } from "../../core/services/browser-storage.service";
import { RealtimeService } from "../../core/services/chat-realtime.service";
import { getDefaultAvatarUrl } from "../../core/constants/avatars.constant";
import { APP_CONSTANTS } from "../../core/constants/app.constants";
import {
  COUNTRY_STORAGE_KEY,
  CountryInfo,
  detectCountry as fetchCountryInfo,
  parseCountryInfo,
} from "../../shared/utils/country-info.util";
import {
  GuestSession,
  GuestSessionRequest,
} from "../../core/models/session.model";
import { Message } from "./components/message-bubble/message-bubble.component";

export abstract class ChatPageController {
  protected abstract messagesArea?: ElementRef<HTMLDivElement>;
  protected abstract realtime: RealtimeService;
  protected abstract persistence: ChatPersistenceService;
  protected abstract storage: BrowserStorageService;
  protected abstract platformId: object;
  protected abstract destroyRef: object;
  protected abstract myCountry: { set(value: string): void; (): string };
  protected abstract country: { set(value: CountryInfo | null): void };
  protected abstract displayName: { set(value: string): void };
  protected abstract avatarUrl: { set(value: string | null): void };
  protected abstract selectedInterests: {
    set(value: string[]): void;
    (): string[];
  };
  protected abstract onboardingOpen: { set(value: boolean): void };
  protected abstract isSearching: { (): boolean };
  protected abstract messages: { set(value: Message[]): void; (): Message[] };
  protected abstract session: GuestSession | null;
  protected abstract profile: GuestSessionRequest | null;
  protected abstract shouldScrollToBottom: boolean;
  protected abstract wasAtBottom: boolean;
  protected abstract messagesResizeObserver?: ResizeObserver;
  protected abstract lastMessagesScrollTop: number;

  protected scrollToBottom(): void {
    const element = this.messagesArea?.nativeElement;
    if (element) {
      element.scrollTop = element.scrollHeight;
      this.lastMessagesScrollTop = element.scrollTop;
    }
    this.wasAtBottom = true;
  }

  protected observeMessagesResize(): void {
    const element = this.messagesArea?.nativeElement;
    if (
      !element ||
      this.messagesResizeObserver ||
      typeof ResizeObserver === "undefined"
    )
      return;
    this.lastMessagesScrollTop = element.scrollTop;
    this.messagesResizeObserver = new ResizeObserver(() => {
      if (this.wasAtBottom) {
        element.scrollTop = element.scrollHeight;
        this.lastMessagesScrollTop = element.scrollTop;
      } else {
        element.scrollTop = this.lastMessagesScrollTop;
      }
      this.lastMessagesScrollTop = element.scrollTop;
    });
    this.messagesResizeObserver.observe(element);
  }

  protected toMessage(message: ChatMessage): Message {
    return {
      id: message.id,
      text: message.text,
      time: new Date(message.sentAt).toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
      }),
      mine: message.senderId === this.realtime.userId,
      ...(message.replyTo ? { replyTo: message.replyTo } : {}),
      ...(message.imageUrl
        ? { imageUrl: message.imageUrl, viewOnce: message.viewOnce }
        : {}),
    };
  }

  protected restoreState(saved: PersistedChatState): void {
    this.session = {
      ...saved.session,
      avatarUrl:
        saved.session.avatarUrl ??
        getDefaultAvatarUrl(saved.session.displayName),
    };
    this.profile = saved.profile;
    this.displayName.set(this.session.displayName);
    this.avatarUrl.set(this.session.avatarUrl ?? null);
    this.selectedInterests.set(saved.selectedInterests);
    this.messages.set(saved.messages as Message[]);
    this.onboardingOpen.set(false);
    if (saved.roomId) {
      this.realtime.hydrateMatch({
        roomId: saved.roomId,
        id: saved.id,
        name: saved.name,
        avatarUrl: saved.avatarUrl,
        country: saved.country ?? "",
        sharedInterests: saved.sharedInterests,
      });
    }
    this.realtime.connect(
      this.session.userId,
      this.session.displayName,
      saved.roomId,
      this.myCountry(),
      this.session.avatarUrl ?? null,
    );
    if (saved.roomId) {
      toObservable(this.realtime.restoreFailed).pipe(
        filter(Boolean),
        take(1),
        takeUntilDestroyed(),
      );
    }
  }

  protected restoreProfile(
    session: GuestSession,
    profile: GuestSessionRequest,
  ): void {
    this.session = {
      ...session,
      avatarUrl: session.avatarUrl ?? getDefaultAvatarUrl(session.displayName),
    };
    this.profile = profile;
    this.displayName.set(this.session.displayName);
    this.avatarUrl.set(this.session.avatarUrl ?? null);
    this.selectedInterests.set(
      profile.interests.slice(0, APP_CONSTANTS.maxInterests),
    );
    this.onboardingOpen.set(false);
    this.realtime.connect(
      this.session.userId,
      this.session.displayName,
      undefined,
      this.myCountry(),
      this.session.avatarUrl ?? null,
    );
    this.startNewChatFromRestore();
  }

  protected abstract startNewChatFromRestore(): void;

  protected detectCountry(): void {
    const cached = parseCountryInfo(this.storage.getItem(COUNTRY_STORAGE_KEY));
    if (cached) {
      this.country.set(cached);
      this.myCountry.set(cached.country_code);
      return;
    }
    fetchCountryInfo().then((value) => {
      if (!value) return;
      this.country.set(value);
      this.myCountry.set(value.country_code);
      this.storage.setItem(COUNTRY_STORAGE_KEY, JSON.stringify(value));
    });
  }

  protected persistState(): void {
    if (!this.session || !this.profile) return;
    this.persistence.write({
      session: this.session,
      profile: this.profile,
      selectedInterests: this.selectedInterests(),
      messages: this.messages(),
      roomId: this.realtime.roomId(),
      id: this.realtime.id(),
      name: this.realtime.name(),
      avatarUrl: this.realtime.avatarUrl(),
      country: this.realtime.country(),
      sharedInterests: this.realtime.sharedInterests(),
      isSearching: this.isSearching(),
    });
  }
}
